'use client';

import { useEffect, useState } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import {
  type ChannelMessageReport,
  subscribeToChannelReports,
} from '../functions/reports/subscribeToChannelReports';

interface State {
  reports: ChannelMessageReport[];
  isLoading: boolean;
  error: Error | undefined;
}

const kInitial: State = {
  reports: [],
  isLoading: true,
  error: undefined,
};

/**
 * Live view of the channel's report queue. Driven directly by a
 * Firestore `onSnapshot` since the report doc is small and the
 * panel benefits from real-time arrivals — moderators see new
 * reports as they land.
 */
export function useChannelReports(channelId: string | null | undefined): State {
  const creatorId = useCurrentUserId();
  const [state, setState] = useState<State>(kInitial);

  useEffect(() => {
    if (!creatorId || !channelId) {
      setState(kInitial);
      return;
    }
    setState(prev => ({ ...prev, isLoading: true, error: undefined }));
    return subscribeToChannelReports(
      creatorId,
      channelId,
      reports => {
        setState({ reports, isLoading: false, error: undefined });
      },
      error => {
        setState({ reports: [], isLoading: false, error });
      },
    );
  }, [creatorId, channelId]);

  return state;
}
